import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Card, CardBody } from '@progress/kendo-react-layout';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { useLocalization } from '@progress/kendo-react-intl';
import { pencilIcon, plusIcon, trashIcon, xIcon } from '@progress/kendo-svg-icons';
import { useQueryClient } from 'react-query';
import type { AnomalyBandWithForm } from './AnomalyBand/types';
import { formatNoteDate } from './AnomalyDetectorForm.utils';
import { useAnomalyBandContext } from './AnomalyBand/AnomalyBandContext';
import type { AnomalyNotePayload } from '../../../Insights/types';
import { enMessages } from '../../../../../../../messages/en-US';
import anomalyApiService from '../../../../Utilities/AnomalyApiService';

interface AnomalyNotesTabProps {
  assetId: string;
  /** Point or device id the band belongs to; used as the bands cache key. */
  targetId: string;
  selectedBandId: string | null;
  selectedBand: AnomalyBandWithForm | undefined;
  userName?: string;
}

const normalizeSearchText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

interface NoteListItem {
  key: string;
  note: AnomalyNotePayload;
  isPending: boolean;
  pendingIndex?: number;
}

export default function AnomalyNotesTab({
  assetId,
  targetId,
  selectedBandId,
  selectedBand,
  userName,
}: AnomalyNotesTabProps): React.ReactElement {
  const ctx = useAnomalyBandContext();
  const setBands = ctx?.setBands;
  const localizationService = useLocalization();
  const queryClient = useQueryClient();
  const isDisabled = !selectedBandId;
  const isPersistedBand = !!selectedBand?.persistedId;
  const notesContainerRef = useRef<HTMLDivElement | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isRefreshingNotes, setIsRefreshingNotes] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState('');
  const [mutatingNoteId, setMutatingNoteId] = useState<string | null>(null);
  // Measured scroll height for the list — fills from the list's top down to just above the
  // form footer. Robust to position/screen size (no magic viewport offset).
  const [listMaxHeight, setListMaxHeight] = useState<number>();
  const notes = useMemo<NoteListItem[]>(
    () =>
      [
        ...(selectedBand?.pendingNotes ?? []).map((note, pendingIndex) => ({
          key: note.id ?? `pending-${note.timestamp}-${pendingIndex}`,
          note,
          isPending: true,
          pendingIndex,
        })),
        ...(selectedBand?.notes ?? []).map((note, index) => ({
          key: note.id ?? `saved-${note.timestamp}-${index}`,
          note,
          isPending: false,
        })),
      ].sort(
        (a, b) => new Date(b.note.timestamp).getTime() - new Date(a.note.timestamp).getTime()
      ),
    [selectedBand?.notes, selectedBand?.pendingNotes]
  );
  const noteDraft = selectedBand?.userNotes ?? '';
  const canAddNote = !isDisabled && noteDraft.trim().length > 0 && !isAddingNote;
  const filteredNotes = useMemo(() => {
    const normalizedSearchValue = normalizeSearchText(searchValue);

    if (!normalizedSearchValue) {
      return notes;
    }

    const searchTerms = normalizedSearchValue.split(' ').filter(Boolean);

    return notes.filter((note) => {
      const searchableText = normalizeSearchText(
        [note.note.user ?? '', note.note.comment ?? ''].join(' ')
      );

      return searchTerms.every((term) => searchableText.includes(term));
    });
  }, [notes, searchValue]);

  useEffect(() => {
    if (notesContainerRef.current) {
      notesContainerRef.current.scrollTop = 0;
    }
  }, [filteredNotes.length, selectedBandId]);

  // Size the list to fill from its top down to just above the form footer, measured live so it
  // works regardless of where it renders (dialog/splitter) and the screen size.
  useEffect(() => {
    const el = notesContainerRef.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top;
      // Fill down to the bottom of the bounded tab content (sits above the dialog footer when there
      // is one, at the viewport bottom otherwise) — no hardcoded footer offset.
      const bounds = el.closest('.k-tabstrip-content');
      const bottom = bounds ? bounds.getBoundingClientRect().bottom : window.innerHeight;
      setListMaxHeight(Math.max(160, bottom - top - 12));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const clearEditingState = () => {
    setEditingNoteId(null);
    setEditingComment('');
  };

  const updateSelectedBand = useCallback((updater: (band: AnomalyBandWithForm) => AnomalyBandWithForm) => {
    if (!selectedBandId || !setBands) return;

    setBands((prev) => prev.map((band) => (band.id === selectedBandId ? updater(band) : band)));
  }, [selectedBandId, setBands]);

  const invalidateNoteQueries = async () => {
    await queryClient.invalidateQueries(['endpointAnomalyBands', targetId]);
    await queryClient.invalidateQueries(['pointAnomalyBands', targetId]);
    await queryClient.invalidateQueries(['pointInsightsAnomalies', targetId]);

    if (assetId) {
      await queryClient.invalidateQueries(['assetInsightsAnomalies', assetId]);
    }
  };

  const loadPersistedNotes = useCallback(async (
    persistedId: string,
    bandType: AnomalyBandWithForm['bandType']
  ) => {
    setIsRefreshingNotes(true);

    try {
      const persistedNotes =
        bandType === 'anchor'
          ? await anomalyApiService.getAnchorNotes(persistedId)
          : await anomalyApiService.getAnomalyNotes(persistedId);

      updateSelectedBand((band) => ({
        ...band,
        notes: persistedNotes,
        pendingNotes: [],
      }));
    } finally {
      setIsRefreshingNotes(false);
    }
  }, [updateSelectedBand]);

  useEffect(() => {
    clearEditingState();

    if (!selectedBandId || !selectedBand?.persistedId) {
      return;
    }

    void loadPersistedNotes(selectedBand.persistedId, selectedBand.bandType);
  }, [loadPersistedNotes, selectedBand?.bandType, selectedBand?.persistedId, selectedBandId]);

  const handleDraftChange = (event: { value?: string }) => {
    if (!selectedBandId || !setBands) return;

    setBands((prev) =>
      prev.map((band) =>
        band.id === selectedBandId
          ? {
              ...band,
              userNotes: event.value ?? '',
            }
          : band
      )
    );
  };

  const handleAddNote = async () => {
    if (!selectedBandId || !setBands || !canAddNote) return;

    const nextNote = {
      timestamp: new Date().toISOString(),
      user:
        userName ??
        selectedBand?.createdByUserName ??
        localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown),
      comment: noteDraft.trim(),
    };

    if (selectedBand?.persistedId) {
      setIsAddingNote(true);

      try {
        if (selectedBand.bandType === 'anchor') {
          await anomalyApiService.createAnchorNote(selectedBand.persistedId, nextNote);
        } else {
          await anomalyApiService.createAnomalyNote(selectedBand.persistedId, nextNote);
        }

        updateSelectedBand((band) => ({
          ...band,
          userNotes: '',
        }));
        await loadPersistedNotes(selectedBand.persistedId, selectedBand.bandType);
        await invalidateNoteQueries();
      } finally {
        setIsAddingNote(false);
      }

      return;
    }

    setBands((prev) =>
      prev.map((band) =>
        band.id === selectedBandId
          ? {
              ...band,
              pendingNotes: [...(band.pendingNotes ?? []), nextNote],
              userNotes: '',
            }
          : band
      )
    );
  };

  const handleStartEditNote = (note: AnomalyNotePayload) => {
    if (!note.id) return;

    setEditingNoteId(note.id);
    setEditingComment(note.comment);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingComment('');
  };

  const handleUpdatePersistedNote = async (noteId: string) => {
    if (!selectedBandId || !selectedBand?.persistedId) return;

    setMutatingNoteId(noteId);

    try {
      if (selectedBand.bandType === 'anchor') {
        await anomalyApiService.updateAnchorNote(selectedBand.persistedId, noteId, {
          comment: editingComment.trim(),
        });
      } else {
        await anomalyApiService.updateAnomalyNote(selectedBand.persistedId, noteId, {
          comment: editingComment.trim(),
        });
      }

      await loadPersistedNotes(selectedBand.persistedId, selectedBand.bandType);
      await invalidateNoteQueries();
      clearEditingState();
    } finally {
      setMutatingNoteId(null);
    }
  };

  const handleDeletePendingNote = (pendingIndex: number) => {
    if (!selectedBandId || !setBands) return;

    setBands((prev) =>
      prev.map((band) =>
        band.id === selectedBandId
          ? {
              ...band,
              pendingNotes: (band.pendingNotes ?? []).filter((_, index) => index !== pendingIndex),
            }
          : band
      )
    );
  };

  const handleDeletePersistedNote = async (noteId: string) => {
    if (!selectedBandId || !selectedBand?.persistedId) return;

    setMutatingNoteId(noteId);

    try {
      if (selectedBand.bandType === 'anchor') {
        await anomalyApiService.deleteAnchorNote(selectedBand.persistedId, noteId);
      } else {
        await anomalyApiService.deleteAnomalyNote(selectedBand.persistedId, noteId);
      }

      await loadPersistedNotes(selectedBand.persistedId, selectedBand.bandType);
      await invalidateNoteQueries();

      if (editingNoteId === noteId) {
        clearEditingState();
      }
    } finally {
      setMutatingNoteId(null);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Add note — same white section pattern as the notes drawer (EntityNoteForm). */}
      <div className="shrink-0 border-b border-gray-200 px-4 py-3">
        <TextArea
          value={noteDraft}
          onChange={handleDraftChange}
          placeholder={localizationService.toLanguageString('custom.addNotePlaceholder', enMessages.custom.addNotePlaceholder)}
          disabled={isDisabled || isAddingNote}
          rows={2}
          style={{ width: '100%' }}
        />
        <div className="mt-2 flex justify-end">
          <Button type="button" themeColor="primary" svgIcon={plusIcon} onClick={handleAddNote} disabled={!canAddNote}>
            {isAddingNote
              ? localizationService.toLanguageString('custom.saving', enMessages.custom.saving)
              : localizationService.toLanguageString('custom.addNoteButton', enMessages.custom.addNoteButton)}
          </Button>
        </div>
      </div>

      {/* Search — on the same gray inset as the list below it. */}
      <div className="shrink-0 bg-[#fafafa] px-3 pb-1 pt-2">
        <div className="relative">
          <Input
            value={searchValue}
            onChange={(event) => setSearchValue(event.value)}
            placeholder={localizationService.toLanguageString('custom.searchNotes', enMessages.custom.searchNotes)}
          />
          {searchValue && (
            <Button
              type="button"
              fillMode="flat"
              themeColor="base"
              size="small"
              svgIcon={xIcon}
              onClick={() => setSearchValue('')}
              className="!absolute right-2 top-1/2 -translate-y-1/2 !min-w-0 !p-1"
            />
          )}
        </div>
      </div>

      <div
        ref={notesContainerRef}
        className="flex w-full min-w-0 min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-[#fafafa] px-3 pb-3"
        style={{ maxHeight: listMaxHeight ? `${listMaxHeight}px` : undefined }}
      >
        {filteredNotes.length > 0 ? (
          filteredNotes.map((listItem) => (
                <Card key={listItem.key} className="w-full shrink-0 overflow-hidden !rounded-lg !border !border-gray-200 !bg-white !shadow-sm">
                  <CardBody className="flex w-full items-stretch justify-between gap-3 !px-4 !py-3">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                      <span className="font-medium text-gray-700">
                        {listItem.note.user || localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown)}
                      </span>
                      <span>{formatNoteDate(listItem.note.timestamp)}</span>
                    </div>
                    {editingNoteId === listItem.note.id ? (
                      <div className="space-y-2">
                        <TextArea
                          value={editingComment}
                          onChange={(event) => setEditingComment(event.value ?? '')}
                          rows={3}
                          style={{ width: '100%' }}
                          disabled={mutatingNoteId === listItem.note.id}
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            themeColor="base"
                            onClick={handleCancelEditNote}
                            disabled={mutatingNoteId === listItem.note.id}
                          >
                            {localizationService.toLanguageString('custom.cancel', enMessages.custom.cancel)}
                          </Button>
                          <Button
                            type="button"
                            themeColor="primary"
                            onClick={() => handleUpdatePersistedNote(listItem.note.id!)}
                            disabled={mutatingNoteId === listItem.note.id || editingComment.trim().length === 0}
                          >
                            {mutatingNoteId === listItem.note.id
                              ? localizationService.toLanguageString('custom.saving', enMessages.custom.saving)
                              : localizationService.toLanguageString('custom.update', enMessages.custom.update)}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap break-words text-sm text-gray-800">
                        {listItem.note.comment}
                      </div>
                    )}
                  </div>
                  {listItem.isPending && listItem.pendingIndex != null && (
                    <div className="flex shrink-0 items-center">
                      <Button
                        type="button"
                        fillMode="flat"
                        themeColor="base"
                        size="small"
                        svgIcon={trashIcon}
                        onClick={() => handleDeletePendingNote(listItem.pendingIndex!)}
                        className="!min-w-0 !p-1 text-gray-500"
                        title={localizationService.toLanguageString('custom.removePendingNote', enMessages.custom.removePendingNote)}
                      />
                    </div>
                  )}
                  {!listItem.isPending && listItem.note.id && editingNoteId !== listItem.note.id && isPersistedBand && !!userName && listItem.note.user === userName && (
                    <div className="flex shrink-0 items-start gap-2">
                      <Button
                        type="button"
                        fillMode="flat"
                        themeColor="base"
                        size="small"
                        svgIcon={pencilIcon}
                        onClick={() => handleStartEditNote(listItem.note)}
                        title={localizationService.toLanguageString('custom.update', enMessages.custom.update)}
                      >
                      </Button>
                      <Button
                        type="button"
                        fillMode="flat"
                        themeColor="base"
                        size="small"
                        svgIcon={trashIcon}
                        onClick={() => handleDeletePersistedNote(listItem.note.id!)}
                        disabled={mutatingNoteId === listItem.note.id}
                        title={localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
                      />
                    </div>
                  )}
                  </CardBody>
                </Card>
          ))
        ) : isRefreshingNotes ? (
          <div className="mt-3 flex min-h-[120px] flex-1 items-center justify-center text-center text-sm text-gray-500">
            {localizationService.toLanguageString('custom.saving', enMessages.custom.saving)}
          </div>
        ) : notes.length > 0 ? (
          <div className="mt-3 flex min-h-[120px] flex-1 items-center justify-center text-center text-sm text-gray-500">
            {localizationService.toLanguageString('custom.noNotesMatchSearch', enMessages.custom.noNotesMatchSearch)}
          </div>
        ) : (
          <div className="mt-3 flex min-h-[120px] flex-1 items-center justify-center text-center text-sm text-gray-500">
            {localizationService.toLanguageString('custom.noNotesYetAddFirst', enMessages.custom.noNotesYetAddFirst)}
          </div>
        )}
      </div>
    </div>
  );
}
